import DNSRepositoryInterface, { DNSZonesInterface } from '@/services/DNS/Interfaces';
import { CertificateInterface, CertificatesRepositoryInterface } from '@/services/SSL/interfaces';
import { MailboxInterface, MailboxRepositoryInterface } from '@/services/Mailbox/interfaces';
import WebsiteRepositoryInterface, { WebsiteComposite } from '@/services/Website/interfaces';
import { PaletteItem } from './types';
import { PaletteSiteContext } from './siteContext';

export interface SiteResourceDeps {
  websiteRepository: WebsiteRepositoryInterface;
  dnsRepository: DNSRepositoryInterface;
  certificateRepository: CertificatesRepositoryInterface;
  mailboxRepository: MailboxRepositoryInterface;
}

function matchesResourceQuery(query: string, keys: string[]): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return keys.some((key) => key.indexOf(q) !== -1 || q.indexOf(key) !== -1);
}

export function buildSiteActionItems(site: PaletteSiteContext, query: string): PaletteItem[] {
  const items: PaletteItem[] = [
    {
      id: `site-action:open:${site.id}`,
      kind: 'action',
      label: `Open ${site.domain}`,
      description: `website #${site.id}`,
      icon: 'mdi-open-in-app',
      routeName: 'website_view',
      routeParams: { id: String(site.id) },
      keywords: ['open', 'website', site.domain],
    },
    {
      id: `site-action:console:${site.id}`,
      kind: 'action',
      label: 'Open in command console',
      description: `/site ${site.id}`,
      icon: 'mdi-console',
      routeName: 'website_console',
      routeQuery: { command: `/site ${site.id}` },
      keywords: ['console', 'site'],
    },
    {
      id: `site-action:dns:${site.id}`,
      kind: 'action',
      label: 'Search DNS for site',
      description: 'Load DNS zone + records',
      icon: 'mdi-routes',
      keywords: ['dns', 'zone', 'records'],
      meta: { siteAction: 'dns' },
    },
    {
      id: `site-action:ssl:${site.id}`,
      kind: 'action',
      label: 'Search SSL for site',
      description: 'Load certificate',
      icon: 'mdi-certificate-outline',
      keywords: ['ssl', 'certificate', 'cert'],
      meta: { siteAction: 'ssl' },
    },
    {
      id: `site-action:mail:${site.id}`,
      kind: 'action',
      label: 'Search mailboxes for site',
      description: 'Load mailboxes',
      icon: 'mdi-email-outline',
      keywords: ['mail', 'mailbox', 'email'],
      meta: { siteAction: 'mail' },
    },
  ];

  if (site.originServer) {
    items.splice(2, 0, {
      id: `site-action:server:${site.id}`,
      kind: 'action',
      label: `Open server ${site.hostname || site.originServer}`,
      description: site.originServer,
      icon: 'mdi-server',
      routeName: 'server_view',
      routeParams: { server: site.originServer },
      keywords: ['server', site.hostname || '', site.originServer],
    });
  }

  const q = query.trim().toLowerCase();
  if (!q) {
    return items;
  }

  return items.filter((item) => {
    const hay = [item.label, item.description || '', ...(item.keywords || [])].join(' ').toLowerCase();
    return hay.indexOf(q) !== -1;
  });
}

export async function loadSiteDnsItems(
  deps: SiteResourceDeps,
  site: PaletteSiteContext,
): Promise<PaletteItem[]> {
  const zones = await deps.dnsRepository.searchZones(site.domain);
  const zone: DNSZonesInterface | null = (zones || []).find((item) => item.websiteId === site.id)
    || (zones || []).find((item) => (item.installation && item.installation.domain === site.domain) || item.domain === site.domain)
    || (zones || [])[0]
    || null;

  if (!zone) {
    return [{
      id: `resource:dns:empty:${site.id}`,
      kind: 'resource',
      label: 'No DNS zone found',
      description: site.domain,
      icon: 'mdi-routes',
      keywords: ['dns'],
    }];
  }

  const records = await deps.dnsRepository.getDNSZoneRecords(zone.id);
  const items: PaletteItem[] = [
    {
      id: `resource:dns:zone:${zone.id}`,
      kind: 'resource',
      label: `DNS zone ${zone.domain}`,
      description: `#${zone.id} · ${zone.status} · ${records.length} records`,
      icon: 'mdi-routes',
      routeName: 'dns_zone_view',
      routeParams: { id: String(zone.id) },
      keywords: ['dns', 'zone', zone.domain],
      meta: { zoneId: zone.id },
    },
  ];

  (records || []).slice(0, 5).forEach((record) => {
    const host = (record.content && (record.content as any).host) || record.target || '';
    items.push({
      id: `resource:dns:record:${record.id}`,
      kind: 'resource',
      label: `${record.type} ${host || zone.domain}`,
      description: `ttl ${(record.content && (record.content as any).ttl) || '—'}`,
      icon: 'mdi-dns',
      routeName: 'dns_zone_view',
      routeParams: { id: String(zone.id) },
      keywords: ['dns', String(record.type), host],
    });
  });

  return items;
}

export async function loadSiteSslItems(
  deps: SiteResourceDeps,
  site: PaletteSiteContext,
): Promise<PaletteItem[]> {
  let composite: WebsiteComposite | null = null;
  try {
    composite = await deps.websiteRepository.getWebsiteById(site.id);
  } catch {
    composite = null;
  }

  const certId = composite && composite.details ? composite.details.sslCertificateId : null;
  let certificate: CertificateInterface | null = null;

  try {
    if (certId !== null && certId !== undefined) {
      certificate = await deps.certificateRepository.getCertificateById(certId);
    } else {
      certificate = await deps.certificateRepository.getCertificate(site.domain);
    }
  } catch {
    certificate = null;
  }

  if (!certificate) {
    return [{
      id: `resource:ssl:empty:${site.id}`,
      kind: 'resource',
      label: 'No SSL certificate',
      description: site.domain,
      icon: 'mdi-certificate-outline',
      routeName: 'website_view',
      routeParams: { id: String(site.id) },
      keywords: ['ssl'],
    }];
  }

  return [{
    id: `resource:ssl:${certificate.id}`,
    kind: 'resource',
    label: `SSL ${certificate.domain}`,
    description: `#${certificate.id} · ${certificate.status} · expires ${certificate.expiresAt || '—'}`,
    icon: 'mdi-certificate-outline',
    routeName: 'website_view',
    routeParams: { id: String(site.id) },
    routeQuery: undefined,
    keywords: ['ssl', 'certificate', certificate.domain, certificate.status],
    meta: { certificateId: certificate.id },
  }, {
    id: `resource:ssl:console:${site.id}`,
    kind: 'action',
    label: 'Manage SSL in console',
    description: `/ssl manage ${site.id}`,
    icon: 'mdi-console',
    routeName: 'website_console',
    routeQuery: { command: `/ssl manage ${site.id}` },
    keywords: ['ssl', 'manage', 'console'],
  }];
}

export async function loadSiteMailItems(
  deps: SiteResourceDeps,
  site: PaletteSiteContext,
): Promise<PaletteItem[]> {
  const [mailboxes] = await deps.mailboxRepository.searchMailbox(1, String(site.id));
  const forSite: MailboxInterface[] = (mailboxes || []).filter((mailbox) => mailbox.websiteId === site.id);

  if (!forSite.length) {
    return [{
      id: `resource:mail:empty:${site.id}`,
      kind: 'resource',
      label: 'No mailboxes',
      description: site.domain,
      icon: 'mdi-email-outline',
      routeName: 'website_view',
      routeParams: { id: String(site.id) },
      keywords: ['mail', 'mailbox'],
    }];
  }

  const items: PaletteItem[] = forSite.slice(0, 5).map((mailbox) => ({
    id: `resource:mail:${mailbox.id}`,
    kind: 'resource' as const,
    label: `Mailbox #${mailbox.id}`,
    description: `${mailbox.server || '—'} · status ${mailbox.status}`,
    icon: 'mdi-email-outline',
    routeName: 'mailbox_list',
    keywords: ['mail', 'mailbox', String(mailbox.id), mailbox.server || ''],
    meta: { mailboxId: mailbox.id },
  }));

  items.push({
    id: `resource:mail:console:${site.id}`,
    kind: 'action',
    label: 'Manage mail in console',
    description: `/mail manage ${site.id}`,
    icon: 'mdi-console',
    routeName: 'website_console',
    routeQuery: { command: `/mail manage ${site.id}` },
    keywords: ['mail', 'manage', 'console'],
  });

  return items;
}

export function shouldAutoLoadResource(query: string): 'dns' | 'ssl' | 'mail' | null {
  const q = query.trim().toLowerCase();
  if (!q) {
    return null;
  }
  if (matchesResourceQuery(q, ['dns', 'zone'])) {
    return 'dns';
  }
  if (matchesResourceQuery(q, ['ssl', 'cert', 'certificate'])) {
    return 'ssl';
  }
  if (matchesResourceQuery(q, ['mail', 'mailbox', 'email'])) {
    return 'mail';
  }
  return null;
}
